import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { payeeAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import DeleteIconButton from './DeleteIconButton';
import './PayeeForm.css';

const emptyAddress = () => ({
  street_1: '',
  street_2: '',
  city: '',
  state: 'IL',
  zip: '',
  phone: '',
  fax: '',
  label: '', // kept for API compatibility; not shown in UI (offices named Office 1, 2, … by order)
});

function PayeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const isEdit = !!id && id !== 'new';
  /** When set (e.g. from Add Provider), return here after create with selectPayeeId in state */
  const returnToPath = location.state?.returnToPath || null;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tax_id: '',
  });
  const [primaryAddress, setPrimaryAddress] = useState(() => ({ ...emptyAddress() }));
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [officesSectionInfoOpen, setOfficesSectionInfoOpen] = useState(false);
  const [moreOfficesInfoOpen, setMoreOfficesInfoOpen] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadPayee();
    }
  }, [id]);

  // Reset auto-save counter when switching payees (same component instance on /payee/:id route).
  useEffect(() => {
    setSaveTrigger(0);
  }, [id]);

  const loadPayee = async () => {
    try {
      setLoading(true);
      const response = await payeeAPI.getById(id);
      const payee = response.data.data;
      setFormData({
        name: payee.name || '',
        email: payee.email || '',
        tax_id: payee.tax_id != null ? String(payee.tax_id) : '',
      });
      const addrs = payee.addresses || [];
      const addr0 = addrs[0];
      setPrimaryAddress(addr0 ? {
        street_1: addr0.street_1 || '',
        street_2: addr0.street_2 || '',
        city: addr0.city || '',
        state: addr0.state || '',
        zip: addr0.zip || '',
        phone: addr0.phone || '',
        fax: addr0.fax || '',
        label: '',
      } : { ...emptyAddress() });
      setAdditionalAddresses(
        addrs.slice(1).map((a) => ({
          street_1: a.street_1 || '',
          street_2: a.street_2 || '',
          city: a.city || '',
          state: a.state || '',
          zip: a.zip || '',
          phone: a.phone || '',
          fax: a.fax || '',
          label: '',
        }))
      );
    } catch (error) {
      console.error('Error loading payee:', error);
      alert('Failed to load payee');
      navigate(tenantPath('/lists/payees'));
    } finally {
      setLoading(false);
    }
  };

  const triggerSave = useCallback(() => {
    if (isEdit) setSaveTrigger((p) => p + 1);
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    triggerSave();
  };

  const handlePrimaryAddressChange = (e) => {
    const { name, value } = e.target;
    setPrimaryAddress((prev) => ({ ...prev, [name]: value }));
    triggerSave();
  };

  const handleAdditionalAddressChange = (index, e) => {
    const { name, value } = e.target;
    setAdditionalAddresses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [name]: value };
      return next;
    });
    triggerSave();
  };

  const addAddress = () => {
    setAdditionalAddresses((prev) => [...prev, { ...emptyAddress() }]);
    triggerSave();
  };

  const removeAddress = (index) => {
    setAdditionalAddresses((prev) => prev.filter((_, i) => i !== index));
    triggerSave();
  };

  const hasAddressContent = (a) =>
    !!(a.street_1 || a.street_2 || a.city || a.state || a.zip || a.phone || a.fax);

  const stripLabelForSave = (a) => ({ ...a, label: '' });

  const performSave = useCallback(async () => {
    const allAddresses = [
      { ...stripLabelForSave(primaryAddress), is_primary: true },
      ...additionalAddresses.map((a) => ({ ...stripLabelForSave(a), is_primary: false })),
    ].filter(hasAddressContent);
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim() || null,
      tax_id: formData.tax_id.trim() || null,
      addresses: allAddresses.length > 0 ? allAddresses : undefined,
    };
    if (isEdit) {
      await payeeAPI.update(id, payload);
      return null;
    }
    const res = await payeeAPI.create(payload);
    return res.data?.data ?? null;
  }, [formData, primaryAddress, additionalAddresses, isEdit, id]);

  useAutoSave(saveTrigger, performSave, { enabled: isEdit, delayMs: 500 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const created = await performSave();
      if (isEdit) {
        navigate(tenantPath('/lists/payees'));
      } else if (created?.id && returnToPath) {
        navigate(returnToPath, {
          replace: true,
          state: { selectPayeeId: created.id, selectPayeeName: created.name || '' },
        });
      } else {
        navigate(tenantPath('/lists/payees'));
      }
    } catch (error) {
      console.error('Error saving payee:', error);
      alert(error.response?.data?.error || 'Failed to save payee');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading">Loading payee...</div>;
  }

  const canEditOffices = !isEdit || hasPermission('payees.update');

  return (
    <div className="payee-form">
      <div className="payee-form-header">
        <div>
          <h1>{isEdit ? 'Edit Payee' : 'Add New Payee'}</h1>
        </div>
        {!isEdit && (
          <div className="payee-form-header-actions">
            <Link to={returnToPath || tenantPath('/lists/payees')} className="btn-cancel">
              Cancel
            </Link>
            <button type="submit" form="payee-form-id" disabled={loading} className="btn-save">
              {loading ? 'Saving...' : 'Create Payee'}
            </button>
          </div>
        )}
      </div>

      <form id="payee-form-id" onSubmit={handleSubmit}>
        <section className="form-section">
          <h2>Name &amp; contact</h2>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Organization or payee name"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="billing@example.org"
            />
          </div>
          <div className="form-group">
            <label htmlFor="payee-tax-id">Tax ID (EIN)</label>
            <input
              id="payee-tax-id"
              type="text"
              name="tax_id"
              value={formData.tax_id}
              onChange={handleChange}
              placeholder="e.g. 12-3456789"
              maxLength={20}
              autoComplete="off"
            />
            <p className="form-hint">Federal employer identification number or similar (optional).</p>
          </div>
        </section>

        <section className="form-section payee-offices-root">
          <div className="payee-offices-root-heading">
            <div className="payee-offices-root-heading-left">
              <h2>Offices</h2>
              <button
                type="button"
                className="payee-field-info-btn"
                aria-expanded={officesSectionInfoOpen}
                aria-controls="payee-offices-root-info"
                onClick={() => setOfficesSectionInfoOpen((o) => !o)}
                title="About offices"
              >
                <span className="payee-field-info-icon" aria-hidden="true">i</span>
                <span className="sr-only">About offices</span>
              </button>
            </div>
            {canEditOffices && (
              <button type="button" className="payee-btn-add-office" onClick={addAddress}>
                <svg className="payee-btn-add-office-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Office
              </button>
            )}
          </div>
          {officesSectionInfoOpen && (
            <div id="payee-offices-root-info" className="payee-field-info-panel" role="region" aria-label="Offices help">
              <p>
                Each <strong>office</strong> is a location for this payee: mailing lines, phone, and fax. Providers link to specific offices on their record so pickers show the right address.
              </p>
              <p>
                Use <strong>Add Office</strong> (top right of this section) to create Office 2, 3, and so on. Office 1 is the first location block below.
              </p>
            </div>
          )}

          <div className="payee-office-card payee-office-card--first">
            <div className="payee-office-card-title-row">
              <h3 className="payee-office-card-title">Office 1</h3>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Street 1</label>
                <input type="text" name="street_1" value={primaryAddress.street_1} onChange={handlePrimaryAddressChange} />
              </div>
              <div className="form-group">
                <label>Street 2</label>
                <input type="text" name="street_2" value={primaryAddress.street_2} onChange={handlePrimaryAddressChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" value={primaryAddress.city} onChange={handlePrimaryAddressChange} />
              </div>
              <div className="form-group">
                <label>State</label>
                <input type="text" name="state" value={primaryAddress.state} onChange={handlePrimaryAddressChange} placeholder="e.g. IL" />
              </div>
              <div className="form-group">
                <label>Zip</label>
                <input type="text" name="zip" value={primaryAddress.zip} onChange={handlePrimaryAddressChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={primaryAddress.phone} onChange={handlePrimaryAddressChange} />
              </div>
              <div className="form-group">
                <label>Fax</label>
                <input type="tel" name="fax" value={primaryAddress.fax} onChange={handlePrimaryAddressChange} />
              </div>
            </div>
          </div>

          <div className="payee-offices-extra-inner">
            <div className="payee-offices-extra-heading">
              <h3 className="payee-office-block-title">More offices</h3>
              <button
                type="button"
                className="payee-field-info-btn"
                aria-expanded={moreOfficesInfoOpen}
                aria-controls="payee-more-offices-info"
                onClick={() => setMoreOfficesInfoOpen((o) => !o)}
                title="About additional offices"
              >
                <span className="payee-field-info-icon" aria-hidden="true">i</span>
                <span className="sr-only">About additional offices</span>
              </button>
            </div>
            {moreOfficesInfoOpen && (
              <div id="payee-more-offices-info" className="payee-field-info-panel" role="region" aria-label="More offices help">
                <p>
                  Each block is another location for this payee. When you edit a provider, you can link them to Office 1, Office 2, etc., from the payee.
                </p>
              </div>
            )}
            {additionalAddresses.length === 0 && (
              <p className="form-hint no-offices-yet">No additional offices yet. Use <strong>Add Office</strong> in the section header when you need a second location.</p>
            )}
            {additionalAddresses.map((addr, index) => (
              <div key={index} className="payee-office-card">
                <div className="payee-office-card-title-row">
                  <h3 className="payee-office-card-title">Office {index + 2}</h3>
                  {canEditOffices && (
                    <DeleteIconButton
                      className="btn-delete"
                      onClick={() => removeAddress(index)}
                      title="Remove this office"
                      aria-label="Remove office"
                    />
                  )}
                </div>
                <div className="form-row">
                <div className="form-group">
                  <label>Street 1</label>
                  <input type="text" name="street_1" value={addr.street_1} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
                <div className="form-group">
                  <label>Street 2</label>
                  <input type="text" name="street_2" value={addr.street_2} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" name="city" value={addr.city} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input type="text" name="state" value={addr.state} onChange={(e) => handleAdditionalAddressChange(index, e)} placeholder="e.g. IL" />
                </div>
                <div className="form-group">
                  <label>Zip</label>
                  <input type="text" name="zip" value={addr.zip} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" name="phone" value={addr.phone} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
                <div className="form-group">
                  <label>Fax</label>
                  <input type="tel" name="fax" value={addr.fax} onChange={(e) => handleAdditionalAddressChange(index, e)} />
                </div>
              </div>
            </div>
          ))}
          </div>
        </section>

        <div className="form-actions">
          {!isEdit && (
            <>
              <Link to={returnToPath || tenantPath('/lists/payees')} className="btn-cancel">
                Cancel
              </Link>
              <button type="submit" disabled={loading} className="btn-save">
                {loading ? 'Saving...' : 'Create Payee'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default PayeeForm;
